import {
  Alert,
  Box,
  Button,
  Form,
  FormField,
  Modal,
  ModalProps,
  NonCancelableCustomEvent,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import { getText } from '@amzn/awscat-editor';
import { Epic, Task } from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import constants from '../../../../common/constants';
import '../../../../resources/styles/scss/components/common/TaskEditorForm.css';
import MultiselectCustomInput from '../../MultiselectCustomInput';
import { TaskSkillOptions } from '../../../utils/SelectableOptions';
import { TaskSkill } from '@amzn/awscat-pattern-library-service-model';
import { getTaskSkills, orderSkillOptions, validateSkills } from '../../../utils/TaskUtils';
import EditorWrapper from '../../../../editor/EditorWrapper';
import { TiptapOutputType } from '@amzn/awscat-editor';
import { DismissModalReason } from '../../../../model/Enum';
import { TiptapMenuType } from '../../../../editor/utils';

const initialState = {
  description: '',
  skills: '',
  skillsError: '',
  title: '',
  titleError: '',
};

interface Props {
  epics: Epic[];
  tasks: Task[];
  editTask?: (t: Task) => void;
  currentEditingTask: Task;
  onDismissClick?: () => void;
}

interface State {
  title: TiptapOutputType;
  titleError: string;
  description: TiptapOutputType;
  skills: string;
  skillsError: string;
  htmlView: boolean;
}

export default class TaskEditorForm extends React.Component<Props, State> {
  private taskSkillOptions: SelectProps.Option[];

  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    const { currentEditingTask } = this.props;
    this.state = {
      ...currentEditingTask,
      htmlView: false,
      skillsError: '',
      titleError: '',
    };
    this.taskSkillOptions = orderSkillOptions(TaskSkillOptions);
  }

  public render() {
    const { onDismissClick } = this.props;
    const { description, htmlView, skills, skillsError, title, titleError } = this.state;

    const formActions = (
      <div className="task-editor-form-actions">
        <Button onClick={onDismissClick} variant="link">
          {constants.CANCEL_BUTTON_TEXT}
        </Button>
        <Button disabled={htmlView} onClick={this.editTask} variant="primary">
          {constants.EDIT_TASK_BUTTON_TEXT}
        </Button>
      </div>
    );

    const taskTitleEditor = (
      <EditorWrapper
        model={title}
        readOnly={false}
        onModelChange={this.updateTaskTitle}
        id={'title-' + this.props.currentEditingTask.id}
        menuType={TiptapMenuType.Simple}
      />
    );
    const taskDescEditor = (
      <EditorWrapper
        model={description}
        readOnly={false}
        onModelChange={this.updateTaskDescription}
        id={'desc-' + this.props.currentEditingTask.id}
      />
    );

    return (
      <div className="form-action task-edit-box">
        <Modal
          className="awsui task-editor-form-modal"
          onDismiss={this.handleDismissModal}
          header={constants.EDIT_TASK_FORM_HEADER}
          visible
          closeAriaLabel="Close modal"
        >
          <Form actions={formActions} data-testid="edit-epic-story-form">
            <SpaceBetween direction={'vertical'} size={'s'}>
              <FormField
                errorText={titleError}
                label={constants.TASK_TITLE_LABEL}
                stretch
                i18nStrings={{ errorIconAriaLabel: 'Error' }}
              >
                {taskTitleEditor}
              </FormField>
              <MultiselectCustomInput
                label={constants.TASK_SKILLS_LABEL}
                customLabel={constants.TASK_SKILLS_CUSTOM_LABEL}
                values={skills}
                selectOptions={this.taskSkillOptions}
                onSelectsUpdated={this.updateTaskSkills}
                onCustomInputUpdated={this.updateTaskSkills}
                placeholder={constants.TASK_SKILLS_PLACEHOLDER}
                customOption={TaskSkill.Other}
                validationError={skillsError}
              />
              <FormField
                label={constants.TASK_DESCRIPTION_LABEL}
                stretch
                i18nStrings={{ errorIconAriaLabel: 'Error' }}
              >
                <Box variant="div" padding={{ top: 's' }}>
                  <div className="editorbox-small task-editor-form-description">
                    {taskDescEditor}
                  </div>
                </Box>
              </FormField>
              <Box margin={{ top: 'xs' }}>
                <Alert type="warning" visible={htmlView}>
                  {constants.EDIT_TASK_WARNING_TEXT}
                </Alert>
              </Box>
            </SpaceBetween>
          </Form>
        </Modal>
      </div>
    );
  }

  private editTask = () => {
    const { currentEditingTask, editTask, onDismissClick } = this.props;
    const { title, skills, description } = this.state;
    const error = this.validateInput();

    if (!error && editTask && onDismissClick) {
      editTask(
        Object.assign(new Task(), {
          ...currentEditingTask,
          description,
          skills,
          title,
        }),
      );
      onDismissClick();
    }
  };

  private handleToggleView = () => {
    const { htmlView } = this.state;
    const newHtmlView = !htmlView;
    this.setState({
      htmlView: newHtmlView,
    });
  };

  private handleDismissModal = (
    dismissEvent: NonCancelableCustomEvent<ModalProps.DismissDetail>,
  ) => {
    const dismissReason = dismissEvent.detail.reason;
    const { onDismissClick } = this.props;
    if (dismissReason === DismissModalReason.CloseButton && onDismissClick) {
      onDismissClick();
    }
  };

  private getPredecessorTasksSelectOptions = (epics: any[]) => {
    return epics.reduce((acc, epic) => {
      // @ts-expect-error TS7006 - strict migration
      const epicTasks = epic.tasks.map((task) => ({
        id: `${epic.title} - ${task.title}`,
        label: task.title,
      }));

      epicTasks.unshift({
        id: `${epic.title}`,
        label: epic.title,
      });

      if (epicTasks.length !== 0) {
        return [
          ...acc,
          {
            id: epic.title,
            label: epic.title,
            options: epicTasks,
          },
        ];
      }

      return acc;
    }, []);
  };

  private updateTaskSkills = (selectValues: string[], custValues: string) => {
    const skills = getTaskSkills(selectValues, custValues);
    this.setState({
      skills,
    });
    this.validateSkills();
  };

  private updateTaskDescription = (content: TiptapOutputType) => {
    this.setState({ description: content });
  };

  private updateTaskTitle = (content: TiptapOutputType) => {
    this.setState({ title: content });
    this.validateTitle();
  };

  private validateTitle = () => {
    const { title } = this.state;
    let error: string = '';

    if (!title || getText(title).length <= 0) {
      error = constants.TASK_TITLE_REQUIRED;
    } else if (getText(title).length > 300) {
      error = constants.TASK_TITLE_TOO_LONG;
    } else if (this.checkDuplicateTask(title)) {
      error = constants.DUPLICATE_TASK_TITLE;
    }

    this.setState({
      titleError: error,
    });

    return error;
  };

  private validateSkills = () => {
    const { skills } = this.state;
    const error = validateSkills(skills);
    this.setState({
      skillsError: error,
    });
    return error;
  };

  private validateInput = () => {
    let error = this.validateTitle();
    if (!error) {
      error = this.validateSkills();
    }
    return error;
  };

  private checkDuplicateTask = (title: TiptapOutputType) => {
    const { currentEditingTask, tasks } = this.props;
    return tasks.some((t) => getText(t.title) === getText(title) && t.id !== currentEditingTask.id);
  };
}
